import { defineNuxtRouteMiddleware, showError, useError } from "nuxt/app";
import { useBugReporter, usePinia } from "#imports";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useDiscountsStore } from "~/stores/discounts";
import { useOutletStore } from "~/stores/outlet";
import { useMenusStore } from "~/stores/menus";

const abortWithUnexpectedError = (
  message: string = "We could not load the location settings. Please try again in a few minutes.",
) => {
  throw showError({
    statusCode: 500,
    statusMessage: message,
  });
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  // FIXME: create the support for outlet slug in ensure-store-is-loaded middleware
  // Skip this middleware if Commerce API is enabled (ensure-store-is-loaded will handle it)
  // const { isEnabled } = useFeatureFlag();
  // const isCommerceApiEnabled = await isEnabled("direct.useCommerceApi");
  // if (isCommerceApiEnabled) return;

  const pinia = usePinia();
  const business = useBusinessStore(pinia);
  const outlet = useOutletStore(pinia);
  const customer = useCustomerStore(pinia);
  const discounts = useDiscountsStore(pinia);
  const menus = useMenusStore(pinia);

  if (!outlet.isLoaded) {
    return abortWithUnexpectedError(
      "EnsureOutletMetadataIsLoadedMiddleware must run after outlet has been loaded only.",
    );
  }

  const routeOrderingMode = (
    Array.isArray(to.params.orderingMode)
      ? to.params.orderingMode[0]
      : to.params.orderingMode
  ) as IDomainOrderingMode;

  if (!routeOrderingMode) {
    return abortWithUnexpectedError(
      "EnsureOutletMetadataIsLoadedMiddleware requires a route with orderingMode parameter.",
    );
  }

  if (
    (outlet.isLoadedOrderingModeSettings &&
      outlet.activeOrderingMode === routeOrderingMode) ||
    (!outlet.hasDelivery && !outlet.hasPickup && !outlet.hasCurbside)
  ) {
    return;
  }

  outlet.patch({
    activeOrderingMode: routeOrderingMode,
  });

  try {
    // Load discounts in background without blocking
    discounts.load(business.id, outlet.id, routeOrderingMode);

    await Promise.all([
      outlet.loadOrderingModeSettings(business.salesChannel),
      customer.trackGoogleConversion(outlet.id, business.salesChannel),
      // Only for parallelism — `resolve-available-menus` is the guaranteed load.
      menus.loadMenus({
        businessId: business.id,
        outletId: outlet.id,
        orderingMode: routeOrderingMode,
        salesChannel: business.salesChannel,
      }),
    ]);
  } catch (error: unknown) {
    useBugReporter().report(error, {
      tags: {
        "code.flag": "middleware-outlet-metadata",
      },
    });

    return abortWithUnexpectedError();
  }
});
